import { mount, closeDialog, dialogOrigin, toast, errorMessage } from "@redstar/core";
const privacySelector = "#shopify-pc__banner, #shopify-pc__prefs";
const badgeSelector = '#trustbadge-iframe, [id^="trustbadge-container-"]';
const visible = element => !!element?.getClientRects().length && getComputedStyle(element).display !== "none" && getComputedStyle(element).visibility !== "hidden";
export function isTextEntry(element) {
  return !!element && !element.disabled && !element.readOnly &&
    (element.matches('textarea, [contenteditable="true"], [contenteditable=""]') ||
      (element.matches("input") && !["radio", "checkbox", "button", "submit", "reset", "hidden", "range", "color", "file"].includes(element.type)));
}
const privacyObservers = new Map();
const badgeStates = new Map();
let privacyOpener, frame;
function syncChrome() {
  frame = null;
  const consentOpen = [...document.querySelectorAll(privacySelector)].some(visible);
  const changed = document.body.dataset.consentUi !== String(consentOpen);
  document.body.dataset.consentUi = String(consentOpen);
  if (consentOpen && changed) {
    document.querySelectorAll("dialog[open]").forEach(dialog => {
      privacyOpener ||= dialogOrigin(dialog);
      closeDialog(dialog, false);
    });
  }
  for (const badge of badgeStates.keys()) if (!badge.isConnected) badgeStates.delete(badge);
  document.querySelectorAll(badgeSelector).forEach(badge => {
    if (consentOpen && !badgeStates.has(badge)) {
      badgeStates.set(badge, badge.inert);
      badge.inert = true;
    } else if (!consentOpen && badgeStates.has(badge)) {
      badge.inert = badgeStates.get(badge);
      badgeStates.delete(badge);
    }
  });
  if (!consentOpen && changed && privacyOpener) {
    const target = privacyOpener;
    privacyOpener = null;
    if (target.isConnected && !target.closest('dialog:not([open]), [inert], [hidden]')) target.focus({ preventScroll: true });
  }
}
const schedule = () => { if (!frame) frame = requestAnimationFrame(syncChrome); };
function discoverPrivacy() {
  for (const [element, observer] of privacyObservers) {
    if (!element.isConnected) { observer.disconnect(); privacyObservers.delete(element); }
  }
  document.querySelectorAll(privacySelector).forEach(element => {
    if (privacyObservers.has(element)) return;
    const observer = new MutationObserver(schedule);
    observer.observe(element, { attributes: true, childList: true, subtree: true, attributeFilter: ["style", "class", "hidden", "aria-hidden"] });
    privacyObservers.set(element, observer);
  });
  schedule();
}
const discovery = new MutationObserver(discoverPrivacy);
discovery.observe(document.body, { childList: true });
discoverPrivacy();
document.addEventListener("visitorConsentCollected", schedule);
document.addEventListener("focusin", event => {
  document.body.dataset.textEntry = String(isTextEntry(event.target));
});
document.addEventListener("focusout", event => {
  document.body.dataset.textEntry = String(isTextEntry(event.relatedTarget));
});
document.addEventListener("click", event => {
  const link = event.target.closest('a[href="#shopifyReshowConsentBanner"]');
  if (!link) return;
  const parent = link.closest("dialog");
  privacyOpener = parent ? dialogOrigin(parent) : link;
  if (parent) closeDialog(parent, false);
  if (!window.privacyBanner?.showPreferences) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  Promise.resolve().then(() => window.privacyBanner.showPreferences()).then(discoverPrivacy).catch(() => {
    privacyOpener?.focus({ preventScroll: true });
    privacyOpener = null;
    toast(errorMessage());
  });
}, true);
document.addEventListener("click", event => {
  const link = event.target.closest("[data-accessibility-info]");
  if (!link) return;
  const details = document.getElementById("AccessibilityInformation");
  if (!details) return;
  event.preventDefault();
  closeDialog(link.closest("dialog"), false);
  details.open = true;
  details.querySelector("summary").focus();
  details.scrollIntoView({ block: "start" });
});
mount("[data-sticky-cart]", sticky => {
  const root = document.documentElement;
  let raf;
  const badgeTargets = new Set();
  const update = () => {
    raf = null;
    const badge = document.querySelector('[id^="trustbadge-container-"] [id^="minimized-trustbadge-"]');
    if (badge && !badgeTargets.has(badge)) { badgeTargets.add(badge); resize?.observe(badge); }
    const rect = badge?.getBoundingClientRect();
    const mobile = matchMedia("(max-width: 47.99rem)").matches;
    if (rect?.width && mobile) root.style.setProperty("--trustbadge-reserve", `${Math.ceil(rect.right + 16)}px`);
    const height = visible(sticky) ? Math.ceil(sticky.getBoundingClientRect().height) : 0;
    root.style.setProperty("--sticky-height", `${height}px`);
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(update); };
  const resize = typeof ResizeObserver === "function" ? new ResizeObserver(queue) : null;
  resize?.observe(sticky);
  const changes = new MutationObserver(queue);
  changes.observe(sticky, { attributes: true, attributeFilter: ["hidden"] });
  changes.observe(document.body, { attributes: true, childList: true, attributeFilter: ["data-consent-ui", "data-text-entry"] });
  document.addEventListener("toggle", queue, true);
  window.addEventListener("resize", queue);
  window.visualViewport?.addEventListener("resize", queue);
  queue();
  return () => {
    cancelAnimationFrame(raf);
    resize?.disconnect(); changes.disconnect();
    document.removeEventListener("toggle", queue, true);
    window.removeEventListener("resize", queue);
    window.visualViewport?.removeEventListener("resize", queue);
    root.style.removeProperty("--sticky-height");
    root.style.removeProperty("--trustbadge-reserve");
  };
});
